//@flow
import React, {useState, useEffect} from 'react';
import {useHistory, useParams} from 'react-router-dom';
import {Alert, AlertTitle} from '@material-ui/lab';

import {makeStyles, Typography, Divider, IconButton} from '@material-ui/core';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import {GetApp, CallMade, ArrowBack, Publish} from '@material-ui/icons';
import {colors} from '../../../../assets/styles/colors';
import {
  useCrmSegmentTemplates,
  useCrmSegmentActions,
  useSegmentDetail,
  useSegmentTasks,
} from '../../utils';
import CustomTextField from '../../../../elements/CustomTextField/CustomTextField';
import InlineLoader from '../../../../elements/LoadingSpinner/InlineLoader';
import TaskResult from './task-result';

const useStyles = makeStyles(theme => ({
  container: {
    width: '100%',
    display: 'flex',
    padding: '1rem',
    flexDirection: 'column',
    gap: '1rem',
  },
  header: {
    maxWidth: 1200,
    background: '#f8f9fb',
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    color: 'black',
  },
  backButton: {
    background: 'white',
    padding: '4px',
    margin: '0 16px 0 0',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
  },
  sectionDescription: {
    marginTop: theme.spacing(2),
    color: colors.grey700,
    fontSize: '0.875rem',
  },
  root: {
    width: '100%',
    padding: '1rem',
    background: 'white',
    boxShadow: '0px 0px 1px rgba(40, 41, 61, 0.08)',
    borderRadius: '.5rem',
    minHeight: '500px',
  },
  sectionContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    gap: '1rem',
    width: '100%',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      alignItems: 'center',
    },
  },
  section: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignContent: 'flex-start',
    gap: '.5rem',
    position: 'relative',
  },
  sectionInformation: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
  },
  subtitle: {
    fontSize: '.875rem',
    fontWeight: 600,
  },
  description: {
    fontSize: '.875rem',
    color: colors.grey700,
  },
  subtitleDescription: {
    fontSize: '.75rem',
    color: colors.grey700,
  },
  divider: {
    margin: '1rem 0',
  },
  uploadArea: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    aspectRatio: '3/2',
    background: colors.grey100,
    borderRadius: '.5rem',
    border: `1px solid ${colors.grey300}`,
    maxHeight: '200px',
    gap: '.5rem',
    flexDirection: 'column',
    color: colors.primary,
    cursor: 'pointer',
  },
  icon: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  caption: {
    fontSize: '.75rem',
    color: colors.grey700,
    textAlign: 'center',
  },
  loader: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 1,
    background: 'rgba(255, 255, 255, .8)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorMessage: {
    color: colors.error,
    fontSize: '.875rem',
    marginBlock: '1rem',
  },
}));

export default function CrmSegmentManualForm() {
  const classes = useStyles();
  const {id} = useParams();

  const history = useHistory();

  const isNew = id === 'new';
  const {segment} = useSegmentDetail(id ?? 'new');
  const {segmentTasks} = useSegmentTasks(id ?? 'new');

  const {
    crmSegmentTemplateLinkCreate,
    crmSegmentTemplateLinkUpdate,
  } = useCrmSegmentTemplates();
  const {
    createManualSegment,
    loading,
    updateManualSegment,
  } = useCrmSegmentActions();
  const [segmentName, setSegmentName] = useState('');
  const [file, setFile] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const lastTask = segmentTasks?.[0];

  useEffect(() => {
    if (!segment || isNew) return;
    setSegmentName(segment.name);
  }, [isNew, segment]);

  const handleSubmit = () => {
    if (!segmentName) {
      setErrorMessage('Por favor ingresa un nombre para tu segmento');
      return;
    }

    if (segment) {
      updateManualSegment(segment.id, segmentName, file).then(result => {
        if (!result) return;
        history.push('/segments');
      });
      return;
    }

    if (!file) {
      setErrorMessage('Por favor sube un archivo');
      return;
    }

    createManualSegment(segmentName, file).then(result => {
      if (!result) return;

      history.push('/segments');
      // window.location.reload();
    });
  };

  return (
    <div className={classes.container}>
      <header className={classes.header}>
        <Typography className={classes.title}>
          <IconButton
            className={classes.backButton}
            onClick={() => {
              history.push('/segments');
            }}
          >
            <ArrowBack />
          </IconButton>
          Segmento Manual
        </Typography>
      </header>
      <aside className={classes.root}>
        <section className={classes.sectionContainer}>
          <div className={classes.sectionInformation}>
            <Typography className={classes.subtitle}>
              1. Descarga la plantilla de excel
            </Typography>
          </div>
          <div>
            <CustomButton
              startIcon={<GetApp />}
              color="primary"
              variant="text"
              href={
                isNew
                  ? crmSegmentTemplateLinkCreate
                  : crmSegmentTemplateLinkUpdate + `/${id}`
              }
              target="_blank"
              // disabled={!hasMassiveProductUploadFeature}
              rel="noopener noreferrer"
            >
              Descargar Excel
            </CustomButton>
          </div>
        </section>

        <Divider className={classes.divider} />

        <section className={classes.sectionContainer}>
          <div className={classes.sectionInformation}>
            <Typography className={classes.subtitle}>
              2. Nombra tu segmento y completa la información
            </Typography>

            <CustomTextField
              label="Nombre de segmento"
              value={segmentName}
              onChange={e => setSegmentName(e.target.value)}
            />
          </div>
          <div>
            <CustomButton endIcon={<CallMade />} color="primary" variant="text">
              Ver Instrucciones
            </CustomButton>
          </div>
        </section>

        <Divider className={classes.divider} />

        <section className={classes.section}>
          {loading && (
            <div className={classes.loader}>
              <InlineLoader loading={loading} />
            </div>
          )}
          <Typography className={classes.subtitle}>
            3. Subir el Excel
          </Typography>
          <Typography className={classes.description}>
            Sube tu plantilla una vez que hayas terminado
          </Typography>
          <Alert severity="warning">
            <AlertTitle>Atención</AlertTitle>
            Las filas que no tengan un número de teléfono válido serán
            ignoradas. Asegúrate de que todas las filas tengan un número
          </Alert>
          <Typography className={classes.subtitleDescription}>
            Por favor, asegúrate de que los números de teléfono en el Excel
            incluyan el prefijo internacional correspondiente.
            <br />
            Por ejemplo, si el número es de Perú, debe comenzar con '+51'.
            Revisa este formato antes de subir el archivo.
          </Typography>
          {lastTask?.state !== 'PENDING' && (
            <label className={classes.uploadArea}>
              <span className={classes.icon}>
                <Publish />
              </span>
              {file ? (
                <span className={classes.label}>Cambiar plantilla</span>
              ) : (
                <span className={classes.label}>Subir plantilla</span>
              )}

              {file && (
                <Typography className={classes.caption}>{file.name}</Typography>
              )}
              <input
                type="file"
                hidden
                accept=".xlsx, .xls"
                disabled={loading}
                onChange={e => {
                  setFile(e.target.files[0]);
                }}
              />
            </label>
          )}

          {lastTask?.state === 'PENDING' && (
            <div className={classes.uploadArea}>
              <strong className={classes.label}>Procesando...</strong>
              <strong className={classes.caption}>
                Puede tardar entre 1 minuto a 2 horas.
              </strong>
              <span className={classes.caption}>
                Puedes salir de esta página sin problemas, mientras tu documento
                sigue procesando
              </span>
            </div>
          )}
        </section>

        <Divider className={classes.divider} />
        {!!lastTask && lastTask?.state !== 'PENDING' && (
          <>
            <Typography className={classes.subtitle}>
              Último Documento Subido
            </Typography>

            <TaskResult task={lastTask} />
          </>
        )}
        {errorMessage && (
          <Typography className={classes.errorMessage}>
            {errorMessage}
          </Typography>
        )}
        <CustomButton
          variant="contained"
          color="primary"
          // fullWidth
          disabled={lastTask?.state === 'PENDING'}
          onClick={handleSubmit}
        >
          Continuar
        </CustomButton>
      </aside>
    </div>
  );
}
